import DOMPurify from 'dompurify';
import * as Linkify from 'linkifyjs';
import { Theme } from '@mui/material';
import linkifyHtml from 'linkify-html';
import 'linkify-plugin-hashtag';
import 'linkify-plugin-mention';
import 'linkify-plugin-ticket';
import 'linkify-plugin-ip';
import MarkdownIt from 'markdown-it';
import { hasMarkdown as detectMarkdown } from './markdownUtils';

// Custom MarkdownIt instance for linkify integration
// breaks: true so single \n (e.g. "Best regards,\nQA Team.") become <br> in HTML; we use whiteSpace: normal for markdown so only HTML line breaks show (no double spacing from literal \n between <p> tags)
const md = new MarkdownIt({ 
  html: true, // Allow HTML tags
  linkify: false, // Disable markdown-it's link detection - we'll use linkifyHtml for better control
  breaks: true // Convert single newlines to <br> so e.g. "Best regards,\nQA Team." displays on two lines
});

const linkifyOptions = {
  defaultProtocol: 'https',
  target: '_blank',
  rel: 'noopener noreferrer',
};

const DOM_PURIFY_CONFIG = {
  ADD_ATTR: ['target', 'rel'],
  ADD_TAGS: ['style', 'ul', 'ol', 'li'],
};

const cleanTextFormater = (text: string) =>
  text ? DOMPurify.sanitize(text, DOM_PURIFY_CONFIG) : '';

/**
 * Scopes HTML email styles to prevent CSS leakage to the rest of the application.
 * All CSS rules are scoped to a specific container class.
 * 
 * @param html - The HTML email content
 * @param scopeClass - The CSS class to scope styles to (default: 'email-content-scope')
 * @returns Scoped HTML with all styles scoped to the container class
 */
export const scopeEmailHtmlStyles = (html: string, scopeClass: string = 'email-content-scope'): string => {
  if (!html || typeof html !== 'string') return html;

  // Use regex to find and scope all <style> tags
  return html.replace(/<style[^>]*>([\s\S]*?)<\/style>/gi, (match: string, cssContent: string) => {
    // Scope CSS selectors - this regex matches selector { declarations }
    // It handles most common CSS patterns including @media queries
    const scopedCss = cssContent
      // First, handle @media queries by scoping rules inside them
      .replace(/(@media[^{]*\{)([\s\S]*?)(\})/g, (mediaMatch: string, mediaStart: string, mediaContent: string, mediaEnd: string) => {
        // Scope selectors inside @media
        const scopedMediaContent = mediaContent.replace(/([^{}@]+)\{([^{}]*)\}/g, (ruleMatch: string, selector: string, declarations: string) => {
          // Skip if already scoped or is an @ rule
          if (selector.includes(scopeClass) || selector.trim().startsWith('@')) {
            return ruleMatch;
          }
          // Scope the selector
          const scopedSelector = selector
            .split(',')
            .map((s: string) => s.trim())
            .filter((s: string) => s.length > 0)
            .map((s: string) => {
              // Don't scope @ rules
              if (s.startsWith('@')) return s;
              // Scope the selector
              return `.${scopeClass} ${s}`;
            })
            .join(', ');
          return `${scopedSelector} {${declarations}}`;
        });
        return `${mediaStart}${scopedMediaContent}${mediaEnd}`;
      })
      // Then handle regular CSS rules (not in @media)
      .replace(/([^{}@]+)\{([^{}]*)\}/g, (ruleMatch: string, selector: string, declarations: string) => {
        // Skip if already scoped, keyframes, or other @ rules
        const trimmedSelector = selector.trim();
        if (trimmedSelector.includes(scopeClass) || 
            trimmedSelector.startsWith('@') || 
            trimmedSelector.includes('@keyframes') ||
            trimmedSelector.includes('@-webkit-keyframes') ||
            trimmedSelector.includes('@-moz-keyframes')) {
          return ruleMatch;
        }
        // Scope the selector
        const scopedSelector = selector
          .split(',')
          .map((s: string) => s.trim())
          .filter((s: string) => s.length > 0)
          .map((s: string) => {
            // Don't scope @ rules
            if (s.startsWith('@')) return s;
            // Scope the selector
            return `.${scopeClass} ${s}`;
          })
          .join(', ');
        return `${scopedSelector} {${declarations}}`;
      });
    
    return `<style>${scopedCss}</style>`;
  });
};

/**
 * Processes HTML email content by scoping styles and linkifying URLs
 * 
 * @param html - The HTML email content
 * @returns Processed and scoped HTML
 */
export const processHtmlEmail = (html: string): string => {
  if (!html || typeof html !== 'string') return html;
  
  // First linkify URLs in the HTML
  const linkified = linkifyHtml(html, linkifyOptions);
  
  // Then scope the styles to prevent leakage
  // The container Box already has the 'email-content-scope' class,
  // so all scoped styles will be contained within it
  const scoped = scopeEmailHtmlStyles(linkified);
  
  // Finally sanitize for security
  return cleanTextFormater(scoped);
};

const LinkifyText = (text: string, type: string = '', theme: Theme) => {
  // Check if text contains markdown syntax using common utility
  const hasMarkdown = detectMarkdown(text);
  
  let result = '';
  
  if (!hasMarkdown) {
    // For plain text or existing HTML (e.g. sent emails with <p>/<br>), ensure </p><p> boundaries get an explicit <br> so spacing is never lost
    let textToLinkify = text;
    if (/<\/p\s*>/i.test(text) && /<p[\s>]/i.test(text)) {
      textToLinkify = text.replace(/<\/p>\s*<p/gi, '</p><br><p');
    }
    result = linkifyHtml(textToLinkify, linkifyOptions);
  } else {
    // For markdown content, render markdown first, then linkify the HTML result
    // This ensures markdown lists and formatting are preserved
    // Normalize line endings to ensure markdown-it can parse lists correctly
    const normalizedText = text.replace(/\r\n/g, '\n').replace(/\r/g, '\n');
    
    // Prevent markdown-it from treating "---" or "===" as Setext heading syntax
    // Setext headings in markdown are created when text is followed by "---" (h2) or "===" (h1) on the next line
    // We need to escape standalone "---" or "===" lines that appear after text to prevent them from being
    // interpreted as heading underlines, which would wrap the entire preceding content in an <h2> or <h1> tag
    // Escape by inserting a zero-width space in the middle (--\u200B- or ==\u200B=)
    // This breaks the Setext heading pattern without being visible in the rendered output
    let escapedText = normalizedText;
    
    // Escape --- (h2 Setext heading) - match standalone lines with exactly 3 dashes
    escapedText = escapedText
      .replace(/\n---\n/g, '\n--\u200B-\n')  // Escape --- on its own line between newlines
      .replace(/^---\n/gm, '--\u200B-\n')    // Escape --- at start of line
      .replace(/\n---$/gm, '\n--\u200B-');   // Escape --- at end of line
    
    // Escape === (h1 Setext heading) - match standalone lines with exactly 3 equals
    escapedText = escapedText
      .replace(/\n===\n/g, '\n==\u200B=\n')  // Escape === on its own line between newlines
      .replace(/^===\n/gm, '==\u200B=\n')    // Escape === at start of line
      .replace(/\n===$/gm, '\n==\u200B=');   // Escape === at end of line
    
    let markdownHtml = md.render(escapedText);
    // Flatten <p> to <br>: use <br><br> for paragraph breaks so spacing is consistent (no <p> margin / literal \n issues)
    markdownHtml = markdownHtml
      .replace(/<\/p>\s*<p[^>]*>/gi, '<br><br>')
      .replace(/<\/p>\s*<(ul|ol|h[1-6]|blockquote|pre|div)/gi, '<br><br><$1')
      .replace(/^<p[^>]*>/i, '')
      .replace(/<\/p>/g, '');
    result = linkifyHtml(markdownHtml, linkifyOptions);
  }
  
  // Add custom styling for links and remove default paragraph margins
  // Styles are scoped to prevent leakage
  const styledHtml = `
    <style>
      .email-content-scope a {
        color: ${theme.palette.primary.main};
        text-decoration: none;
      }
      .email-content-scope a:hover {
        text-decoration: underline;
      }
      .email-content-scope p {
        margin: 0;
        padding: 0;
      }
      .email-content-scope p + p {
        margin-top: 0.5em;
      }
      .email-content-scope h1, .email-content-scope h2, .email-content-scope h3, .email-content-scope h4, .email-content-scope h5, .email-content-scope h6 {
        margin-top: 0.5em;
        margin-bottom: 0.5em;
        font-weight: normal;
      }
      .email-content-scope h1:first-child, .email-content-scope h2:first-child, .email-content-scope h3:first-child, .email-content-scope h4:first-child, .email-content-scope h5:first-child, .email-content-scope h6:first-child {
        margin-top: 0;
      }
      .email-content-scope ul, .email-content-scope ol {
        margin: 0.5em 0;
        padding-left: 1.5em;
        list-style-position: outside;
      }
      .email-content-scope ul {
        list-style-type: disc;
      }
      .email-content-scope ol {
        list-style-type: decimal;
      }
      .email-content-scope li {
        margin: 0.25em 0;
        display: list-item;
      }
      .email-content-scope blockquote {
        margin: 0.5em 0;
        padding-left: 1em;
        border-left: 3px solid #ccc;
      }
      .email-content-scope pre {
        margin: 0.5em 0;
      }
      .email-content-scope code {
        background-color: rgba(0, 0, 0, 0.05);
        padding: 0.2em 0.4em;
        border-radius: 3px;
      }
      .email-content-scope strong {
        font-weight: bold;
      }
      .email-content-scope em {
        font-style: italic;
      }
    </style>
    <div class="email-content-scope">${result}</div>
  `;
  
  // Sanitize and return
  return cleanTextFormater(styledHtml);
};

export default LinkifyText;
